"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Scale, FileText, ShieldAlert, Hammer, CircleCheck as CheckCircle2, ArrowRight, CircleQuestionMark as HelpCircle, FileCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function Terms() {
  const shouldReduceMotion = useReducedMotion();
  const [activeItem, setActiveItem] = useState<string>("item-1");
  const termsSections = [{
    id: "item-1",
    number: "01",
    icon: FileText,
    title: "Website usage terms",
    summary: "Permitted use of our digital resources, contact forms, and online portfolio materials.",
    content: "By accessing this website, you agree to browse and utilize the content solely for personal, non-commercial purposes related to planning residential remodeling in Decatur, Georgia and adjacent communities. You agree not to scrape, copy, or redistribute architectural photographs, project write-ups, or proprietary planning documents without written consent."
  }, {
    id: "item-2",
    number: "02",
    icon: Hammer,
    title: "Renovation estimates and pricing disclaimers",
    summary: "Understanding preliminary budget ranges versus finalized construction agreements.",
    content: "All preliminary estimates, cost guides, and budget ranges displayed on this site or provided via introductory phone consults are non-binding estimates based on prevailing regional material and labor costs. Formal project pricing is finalized only upon on-site structural inspection, detailed architectural design approval, and execution of a signed construction contract."
  }, {
    id: "item-3",
    number: "03",
    icon: FileCheck,
    title: "Intellectual property and architectural plans",
    summary: "Ownership of bespoke blueprints, 3D renderings, and design concepts.",
    content: "All floor plans, elevation diagrams, finish schedules, 3D spatial models, and photographic documentation produced by Decatur Heritage Renovations remain the exclusive intellectual property of the company until fully conveyed through specific design-build contract provisions. Unauthorized use for self-construction or third-party bidding is prohibited."
  }, {
    id: "item-4",
    number: "04",
    icon: ShieldAlert,
    title: "Limitation of liability",
    summary: "Scope parameters, liability boundaries, and project agreement terms.",
    content: "All project terms, scope parameters, and responsibilities are defined exclusively within executed written agreements between Decatur Heritage Renovations and the client. Website information is provided 'as is' without implied guarantees regarding municipal zoning approvals or historical district variances, which are evaluated independently for each parcel."
  }];
  const highlights = [{
    title: "Transparent scope",
    description: "Itemized line-item contracts before any work begins."
  }, {
    title: "Local compliance",
    description: "Permitted strictly under City of Decatur and DeKalb County codes."
  }];
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="terms-of-service" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="mx-auto max-w-3xl text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Badge variant="outline" className="border-border bg-secondary text-secondary-foreground text-xs uppercase tracking-wider font-semibold py-1 px-3">
              Legal &amp; Policies
            </Badge>
          </div>

          <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Terms of service
          </h2>

          <p className="mt-4 font-sans text-base text-muted-foreground sm:text-lg leading-relaxed">
            Please review our foundational policies on project estimates, website usage, intellectual property, and residential remodeling agreements.
          </p>

          <div className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground font-sans">
            <span>Last updated: March 2025</span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>Decatur, Georgia</span>
          </div>
        </motion.div>

        {/* Core Content Grid */}
        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main Accordion Terms */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants} className="lg:col-span-8">
            <Card className="border border-border bg-card/85 backdrop-blur-md shadow-xs overflow-hidden py-0">
              <CardHeader className="p-6 sm:p-8 border-b border-border bg-secondary/30">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-primary border border-border">
                    <Scale className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="font-serif text-xl sm:text-2xl text-card-foreground">
                      Service agreements &amp; legal standards
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm mt-1">
                      Click any topic below to expand the full policy terms.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 sm:p-8">
                <Accordion type="single" collapsible defaultValue="item-1" value={activeItem} onValueChange={val => setActiveItem(val)} className="w-full space-y-4">
                  {termsSections.map((section, idx) => {
                  const IconComp = section.icon;
                  return <AccordionItem key={section.id} value={section.id} data-index={idx} className="rounded-lg border border-border bg-background/60 px-4 sm:px-6 transition-[border-color,background-color] duration-200 hover:border-primary/40 data-[state=open]:border-primary/40 data-[state=open]:bg-secondary/20">
                        <AccordionTrigger className="py-4 sm:py-5 hover:no-underline">
                          <div className="flex items-start sm:items-center gap-3 sm:gap-4 text-left">
                            <span className="font-mono text-xs font-semibold text-muted-foreground shrink-0 mt-0.5 sm:mt-0">
                              {section.number}
                            </span>
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-secondary text-foreground">
                              <IconComp className="h-4 w-4" />
                            </div>
                            <div>
                              <h3 className="font-sans text-base sm:text-lg font-semibold text-foreground">
                                {section.title}
                              </h3>
                              <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">
                                {section.summary}
                              </p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 pb-5 text-sm sm:text-base text-muted-foreground leading-relaxed">
                          <Separator className="mb-4" />
                          <p>{section.content}</p>
                        </AccordionContent>
                      </AccordionItem>;
                })}
                </Accordion>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sidebar & Quick Reference */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants} className="lg:col-span-4 space-y-6">
            {/* Architectural Trust Points */}
            <Card className="border border-border bg-card shadow-xs py-6">
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg text-card-foreground">
                  Contract safeguards
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Our standard protocol for every residential remodeling engagement in Decatur.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                {highlights.map((item, idx) => <div key={item.title} data-index={idx} className="flex items-start gap-3 rounded-md bg-secondary/40 p-3 border border-border">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {item.title}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>

            {/* Inquiries / Questions Card */}
            <Card className="border border-border bg-secondary/60 shadow-xs py-6">
              <CardHeader className="pb-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-background border border-border text-foreground mb-2">
                  <HelpCircle className="h-5 w-5" />
                </div>
                <CardTitle className="font-serif text-lg text-foreground">
                  Have questions about our terms?
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Reach out to our project planning team for contract walk-throughs or estimate clarifications.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-2">
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>
                    <strong className="text-foreground">Phone:</strong> (470) 847-9076
                  </p>
                  <p>
                    <strong className="text-foreground">Email:</strong> serviceleadalerts@gmail.com
                  </p>
                  <p>
                    <strong className="text-foreground">Office:</strong> Decatur, Georgia 30030
                  </p>
                </div>
                <Separator className="my-3" />
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs">
                  <Link to="#">
                    <span>Contact our team</span>
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}